import * as React from "react";
import { LandListItem } from "../../../types/donations";
import { useNavigate } from "react-router-dom";

type Props = {
  item: LandListItem;
  className?: string;
  onViewMap?: (it: LandListItem) => void;
  onViewDetails?: (it: LandListItem) => void;
};

const fmtNum = (n: number | null | undefined) =>
  typeof n === "number" ? n.toLocaleString("en-US") : "—";

const LandCard: React.FC<Props> = ({ item, className, onViewMap, onViewDetails }) => {
  const navigate = useNavigate();

  const priorityMeta = (p?: number) => {
    switch (p) {
      case 3: return { label: "أولوية عالية",  cls: "bg-emerald-100 text-emerald-700" };
      case 2: return { label: "أولوية متوسطة", cls: "bg-amber-600 text-white" };
      default: return { label: "أولوية منخفضة",   cls: "bg-emerald-600 text-white" };
    }
  };
  const pri = priorityMeta(item.donationPriority);

  const canShowMap = typeof item.latitude === "number" && typeof item.longitude === "number";

  const goDetails = () => {
    onViewDetails?.(item);
    navigate(`/benefaction/lands/${item.id}`, { state: { fromList: true } });
  };

  return (
    <div className="h-full">
      {/* Home Page Style Card - Matching Mosque Search */}
      <article className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 flex flex-col h-full relative">
        {/* Top-right badge - Priority */}
        <div className="absolute top-4 end-4 z-10">
          <span className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full shadow-sm ${pri.cls}`}>
            <i className="hgi-stroke hgi-star text-xs" />
            {pri.label}
          </span>
        </div>

        {/* Icon - Home Page Style */}
        <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            className="w-10 h-10 text-sa-600"
            aria-label="أيقونة الأراضي الشاغرة"
            role="img"
          >
            <path d="M3 19h18" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M5 19v-5l3-2 4 3 4-2 3 2v4" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M8 9h8" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M10 5h4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        {/* Title - Home Page Style */}
        <h3 className="text-xl font-bold text-gray-900 mb-3 text-right leading-tight line-clamp-2 min-h-[56px]">
          <button 
            className="hover:text-sa-600 transition-colors text-right" 
            onClick={goDetails}
          >
            {item.fullAddress || "—"}
          </button>
        </h3>

        {/* Details Grid */}
        <div className="space-y-2.5 flex-1 mb-4">
          {/* Serial Number */}
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-gray-500 text-sm font-medium">رقم التسلسل</span>
            <span className="text-gray-900 font-semibold text-sm">
              {item.sequence != null ? `#${item.sequence}` : "—"}
            </span>
          </div>

          {/* Plot Number */}
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-gray-500 text-sm font-medium">رقم القطعة</span>
            <span className="text-gray-900 font-semibold text-sm">{item.landNumber || "—"}</span>
          </div>

          {/* Area */}
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-gray-500 text-sm font-medium">المساحة (م²)</span>
            <span className="text-gray-900 font-semibold text-sm">
              {fmtNum(item.buildingArea)} متر مربع
            </span>
          </div>

          {/* Estimated Price */}
          <div className="flex items-center justify-between py-2">
            <span className="text-gray-500 text-sm font-medium">السعر التقديري</span>
            <div className="flex items-center gap-1">
              <span className="text-sa-600 font-bold text-sm">{fmtNum(item.aproxLandPrice)}</span>
              <img
                className="inline-block w-3 h-3 align-[-2px]"
                src={`${process.env.PUBLIC_URL}/dga/assets/icons/riyal-sar.svg`}
                alt="ريال سعودي"
                onError={(e) => {
                  e.currentTarget.replaceWith(
                    Object.assign(document.createElement("span"), {
                      textContent: "ر.س",
                      ariaLabel: "ريال سعودي",
                      className: "inline-block text-xs leading-none align-[-2px]",
                    })
                  );
                }}
              />
            </div>
          </div>
        </div>

        {/* Footer with Buttons - Home Page Style */}
        <div className="mt-auto pt-4 border-t border-gray-100">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={goDetails}
              className="w-full h-11 flex items-center justify-center gap-2 rounded-lg bg-sa-600 text-white text-sm font-semibold hover:bg-sa-700 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-sa-500 focus:ring-offset-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              <span>عرض التفاصيل</span>
            </button>

            <button
              type="button"
              onClick={() => canShowMap && onViewMap?.(item)}
              disabled={!canShowMap}
              className={`w-full h-11 flex items-center justify-center gap-2 rounded-lg border text-sm font-semibold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 ${
                canShowMap
                  ? "border-gray-300 text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400"
                  : "border-gray-200 text-gray-300 bg-gray-50 cursor-not-allowed"
              }`}
              title={canShowMap ? "عرض الخريطة" : "الخريطة غير متاحة"}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>عرض الخريطة</span>
            </button>
          </div>
        </div>
      </article>
    </div>
  );
};

export default LandCard;
